import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { ShoppingCart, Plus, X, Trash2, Store as StoreIcon, MapPin } from "lucide-react";
import PayButton from "../components/PayButton";
import useGeo from "../hooks/useGeo";
import GeoToggle from "../components/GeoToggle";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "food", label: "Food" },
  { id: "toy", label: "Toys" },
  { id: "health", label: "Health" },
  { id: "grooming", label: "Grooming" },
];

export default function Store() {
  const [cat, setCat] = useState("all");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({ items: [], total: 0, store_id: null, store_name: null });
  const [drawer, setDrawer] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [conflict, setConflict] = useState(null); // { current_store_name, incoming_store_id, incoming_store_name, product_id }
  const geo = useGeo(25);

  const load = () => {
    const params = { ...(cat === "all" ? {} : { category: cat }), ...geo.geoParams };
    axios.get(`${API}/products`, { params }).then(r => setProducts(r.data));
    axios.get(`${API}/cart`).then(r => setCart(r.data));
  };
  useEffect(load, [cat, geo.mode, geo.coords]);

  // Group products by store so the user sees clearly which store an item belongs to.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of products) {
      const sid = p.store_id || "marketplace";
      if (!buckets.has(sid)) {
        buckets.set(sid, {
          store_id: sid,
          store_name: p.store_name || "PetBond Marketplace",
          store_city: p.store_city || p.city || "",
          distance_km: p.distance_km,
          items: [],
        });
      }
      buckets.get(sid).items.push(p);
    }
    // Sort by distance, then alphabetically
    return Array.from(buckets.values()).sort((a, b) => {
      const da = a.distance_km ?? 9999;
      const db = b.distance_km ?? 9999;
      if (da !== db) return da - db;
      return (a.store_name || "").localeCompare(b.store_name || "");
    });
  }, [products]);

  const add = async (product_id) => {
    try {
      const r = await axios.post(`${API}/cart`, { product_id, quantity: 1 });
      if (r.data?.cart_conflict) {
        setConflict({ ...r.data, product_id });
        return;
      }
      const c = await axios.get(`${API}/cart`);
      setCart(c.data);
    } catch (e) {
      // unexpected
      console.warn(e?.message);
    }
  };

  const confirmSwitchStore = async () => {
    if (!conflict) return;
    await axios.post(`${API}/cart/replace`, { product_id: conflict.product_id, quantity: 1 });
    const c = await axios.get(`${API}/cart`);
    setCart(c.data);
    setConflict(null);
  };

  const remove = async (id) => {
    await axios.delete(`${API}/cart/${id}`);
    const r = await axios.get(`${API}/cart`);
    setCart(r.data);
  };

  const checkout = async () => {
    try {
      const itemsPayload = cart.items.map(it => ({
        product_id: it.product.product_id,
        name: it.product.name,
        price: it.product.price,
        quantity: it.quantity,
      }));
      if (itemsPayload.length > 0) {
        const address = window.prompt("Delivery address?") || "Default address";
        await axios.post(`${API}/orders`, { items: itemsPayload, delivery_address: address });
      }
    } catch (e) {
      console.warn("New order flow failed, falling back", e?.message);
    }
    const r = await axios.post(`${API}/checkout`);
    setConfirmed(r.data.order_id);
  };

  const afterPay = async () => {
    const c = await axios.get(`${API}/cart`);
    setCart(c.data);
    setTimeout(() => { setConfirmed(null); setDrawer(false); }, 1600);
  };

  const count = cart.items.reduce((s, it) => s + it.quantity, 0);

  return (
    <div className="space-y-6" data-testid="store-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-extrabold">Pet Store</h1>
        <div className="flex items-center gap-3">
          <GeoToggle geo={geo} label="stores" />
          <button
            data-testid="open-cart-button"
            onClick={() => setDrawer(true)}
            className="relative bg-ink text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-brand transition-colors"
          >
            <ShoppingCart className="w-4 h-4" /> Cart
            {count > 0 && <span className="bg-brand text-white rounded-full text-xs font-bold px-2 py-0.5 ml-1">{count}</span>}
          </button>
        </div>
      </div>

      {cart.store_id && cart.items.length > 0 && (
        <div className="bg-sun/40 border border-line rounded-2xl px-4 py-3 text-sm font-bold inline-flex items-center gap-2" data-testid="cart-store-lock-banner">
          <StoreIcon className="w-4 h-4"/> Shopping from <span className="text-brand">{cart.store_name}</span> · adding from another store will ask you to clear cart first.
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            data-testid={`filter-${c.id}`}
            onClick={() => setCat(c.id)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold border transition-colors ${cat === c.id ? "bg-brand text-white border-brand" : "bg-surface border-line text-ink-soft hover:border-brand"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {geo.isNearby && grouped.length === 0 && (
        <div className="bg-surface-muted border border-line rounded-2xl p-6 text-center text-sm" data-testid="store-empty-nearby">
          <div className="font-bold mb-1">No stores within {geo.radius} km of you yet.</div>
          <p className="text-ink-soft">Switch to <button onClick={() => geo.setMode("all")} className="underline font-bold">All India</button> to shop from every partner store.</p>
        </div>
      )}

      {grouped.map(group => (
        <section key={group.store_id} data-testid={`store-group-${group.store_id}`} className="space-y-3">
          <header className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-heading text-xl font-extrabold inline-flex items-center gap-2">
                <StoreIcon className="w-4 h-4 text-brand"/> {group.store_name}
              </h2>
              <div className="text-xs text-ink-soft flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3"/>{group.store_city || "PetBond"}{group.distance_km != null && <span className="ml-1 font-bold text-brand">· {group.distance_km} km away</span>}
              </div>
            </div>
            <span className="text-xs text-ink-soft font-bold">{group.items.length} product{group.items.length === 1 ? "" : "s"}</span>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {group.items.map(p => (
              <article key={p.product_id} data-testid={`product-${p.product_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all flex flex-col">
                <img src={p.photo} alt={p.name} className="w-full h-36 object-cover" />
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft">{p.category}</div>
                  <h3 className="font-heading font-bold mt-1 line-clamp-2">{p.name}</h3>
                  <p className="text-xs text-ink-soft mt-1 line-clamp-2">{p.description}</p>
                  <div className="mt-auto pt-3 flex items-center justify-between">
                    <span className="font-heading font-extrabold text-lg">₹{p.price}</span>
                    <button data-testid={`add-to-cart-${p.product_id}`} onClick={() => add(p.product_id)} className="bg-brand text-white rounded-full w-9 h-9 grid place-items-center hover:bg-brand-hover">
                      <Plus className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}

      {drawer && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setDrawer(false)}>
          <aside onClick={e=>e.stopPropagation()} className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-surface p-6 overflow-y-auto" data-testid="cart-drawer">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-2xl font-extrabold">Your cart</h3>
              <button onClick={() => setDrawer(false)} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>
            {cart.store_name && cart.items.length > 0 && (
              <div className="mb-4 text-sm text-ink-soft inline-flex items-center gap-1.5" data-testid="cart-drawer-store">
                <StoreIcon className="w-3.5 h-3.5"/> from <span className="font-bold text-ink">{cart.store_name}</span>
              </div>
            )}
            {cart.items.length === 0 && !confirmed && <div className="text-ink-soft text-sm">Your cart is empty.</div>}
            {confirmed && <div className="bg-leaf/30 rounded-2xl p-4 font-bold">Order confirmed! 🎉<div className="text-xs font-normal mt-1">{confirmed}</div></div>}
            <div className="space-y-3">
              {cart.items.map(it => (
                <div key={it.product.product_id} className="flex gap-3 items-center bg-surface-muted rounded-2xl p-3">
                  <img src={it.product.photo} className="w-14 h-14 rounded-xl object-cover" alt="" />
                  <div className="flex-1">
                    <div className="font-bold text-sm">{it.product.name}</div>
                    <div className="text-xs text-ink-soft">Qty {it.quantity} · ₹{it.line_total}</div>
                  </div>
                  <button data-testid={`remove-${it.product.product_id}`} onClick={() => remove(it.product.product_id)} className="p-2 hover:text-danger"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
            {cart.items.length > 0 && !confirmed && (
              <div className="mt-6 border-t border-line pt-4">
                <div className="flex justify-between mb-4">
                  <span className="font-bold">Total</span>
                  <span className="font-heading text-xl font-extrabold">₹{cart.total}</span>
                </div>
                <PayButton
                  amount={cart.total}
                  currency="INR"
                  country="IN"
                  purpose="store_order"
                  label={`Pay ₹${cart.total} & checkout`}
                  dataTestid="checkout-button"
                  onSuccess={() => checkout().then(afterPay)}
                />
              </div>
            )}
          </aside>
        </div>
      )}

      {conflict && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={() => setConflict(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-sm border border-line shadow-soft" data-testid="cart-conflict-dialog">
            <div className="text-3xl mb-2">🛒</div>
            <h4 className="font-heading text-xl font-extrabold">Start a new cart?</h4>
            <p className="text-sm text-ink-soft mt-2">
              Your cart already has items from <span className="font-bold text-ink">{conflict.current_store_name}</span>. PetBond carts hold items from one store at a time so you get one delivery and one bill.
            </p>
            <p className="text-sm text-ink-soft mt-2">
              Clear cart and start shopping from <span className="font-bold text-ink">{conflict.incoming_store_name}</span>?
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConflict(null)} data-testid="cart-conflict-cancel" className="flex-1 rounded-full border border-line py-2.5 text-sm font-bold hover:bg-surface-muted">Keep current cart</button>
              <button onClick={confirmSwitchStore} data-testid="cart-conflict-confirm" className="flex-1 bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover">Clear & switch</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
